import { useState } from 'react';

import { Button } from '../../button';

import {
  APPROVERS,
  approvalShows,
  approverLabels,
  AUDIT_COMMAND,
  AUDIT_REFINEMENT,
  auditRowsChanged,
  auditSummary,
  auditTable,
  auditTransition,
  BADGES_PROPOSAL,
  commandLabels,
  commandRuns,
  COMMANDS,
  DECISIONS,
  decisionRuns,
  initialAudit,
  initialPause,
  initialRating,
  initialResume,
  loopActions,
  lookupsText,
  PAUSE_REFINEMENT,
  pauseSummary,
  pauseTransition,
  permitted,
  proposalsText,
  RATING_COUNTEREXAMPLE,
  RATING_REFINEMENT,
  ratings,
  ratingSummary,
  ratingTransition,
  RAVI_NOTE,
  replyLabel,
  RESUME_COUNTEREXAMPLE,
  RESUME_REFINEMENT,
  resumeSummary,
  resumeTransition,
  runsBeforeApproval,
  toApprover,
  toCommand,
  toDecision,
  TOOLS,
  toTool,
  WRITES_MADE,
  type AuditAction,
  type PauseAction,
  type RatingAction,
  type ResumeAction,
} from './approve-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';

export function ApproveRatingLab() {
  const [state, setState] = useState(initialRating);
  const act = (action: RatingAction) => setState((current) => ratingTransition(current, action));
  const rating = ratings[state.tool];
  const summary = ratingSummary(state);
  return (
    <Lab
      title="What may run at once, and what must wait?"
      eyebrow="Approval · rate each tool"
      className="rel-lab approve-lab"
    >
      <Predict>What may run at once, and what must wait? Choose a Tool, then press Rate.</Predict>
      <div className="cb-controls">
        <label>
          Tool
          <ModelSelect
            aria-label="Tool"
            value={state.tool}
            onValueChange={(value) => act({ type: 'tool', value: toTool(value) })}
            options={TOOLS.map((tool) => ({ value: tool, label: tool }))}
          />
        </label>
        <Button size="sm" disabled={state.rated} onClick={() => act({ type: 'rate' })}>
          Rate
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="What the approval shows">
        <pre aria-label="What the approval shows">
          {state.rated ? approvalShows[state.tool] : '(not rated yet)'}
        </pre>
      </Panel>
      {state.rated ? (
        <Facts
          values={[
            ['Rating', rating],
            ['The loop', loopActions[rating]],
            ['Runs before approval', runsBeforeApproval(state.tool)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.rated ? (
        <p className="rel-caption">
          {RATING_COUNTEREXAMPLE} {RATING_REFINEMENT}
        </p>
      ) : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ApprovePauseLab() {
  const [state, setState] = useState(initialPause);
  const act = (action: PauseAction) => setState((current) => pauseTransition(current, action));
  const run = commandRuns[state.command];
  const summary = pauseSummary(state);
  return (
    <Lab
      title="What does the assistant do with a command?"
      eyebrow="Approval · the model proposes, the run pauses"
      className="rel-lab approve-lab"
    >
      <Predict>
        What does the assistant do with a command? Choose a Command, then press Send.
      </Predict>
      <div className="cb-controls">
        <label>
          Command
          <ModelSelect
            aria-label="Command"
            value={state.command}
            onValueChange={(value) => act({ type: 'command', value: toCommand(value) })}
            options={COMMANDS.map((command) => ({
              value: command,
              label: commandLabels[command],
            }))}
          />
        </label>
        <Button size="sm" disabled={state.sent} onClick={() => act({ type: 'send' })}>
          Send
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="Lookups">
        <pre aria-label="Lookups">{state.sent ? lookupsText(run) : '(not sent yet)'}</pre>
      </Panel>
      <Panel label="Proposals">
        <pre aria-label="Proposals">{state.sent ? proposalsText(run) : '(not sent yet)'}</pre>
      </Panel>
      <Panel label={state.sent ? replyLabel(run) : "The model's reply"}>
        <pre aria-label="The model's reply">{state.sent ? run.reply : '(not sent yet)'}</pre>
      </Panel>
      {state.sent ? (
        <Facts
          values={[
            ['Lookups', String(run.lookups.length)],
            ['Proposals', String(run.proposals.length)],
            ['Writes made', WRITES_MADE],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.sent ? <p className="rel-caption">{PAUSE_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ApproveResumeLab() {
  const [state, setState] = useState(initialResume);
  const act = (action: ResumeAction) => setState((current) => resumeTransition(current, action));
  const run = decisionRuns[state.decision];
  const summary = resumeSummary(state);
  return (
    <Lab
      title="What happens after Maya decides?"
      eyebrow="Approval · approve or reject, then resume"
      className="rel-lab approve-lab"
    >
      <Predict>What happens after Maya decides? Choose a Decision, then press Decide.</Predict>
      <div className="cb-controls">
        <label>
          Decision
          <ModelSelect
            aria-label="Decision"
            value={state.decision}
            onValueChange={(value) => act({ type: 'decision', value: toDecision(value) })}
            options={DECISIONS.map((decision) => ({ value: decision, label: decision }))}
          />
        </label>
        <Button size="sm" disabled={state.decided} onClick={() => act({ type: 'decide' })}>
          Decide
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The proposal">
        <pre aria-label="The proposal">{BADGES_PROPOSAL}</pre>
      </Panel>
      <Panel label="The tool result sent back">
        <pre aria-label="The tool result sent back">
          {state.decided ? run.result : '(not decided yet)'}
        </pre>
      </Panel>
      <Panel label="The model's closing sentence">
        <pre aria-label="The model's closing sentence">
          {state.decided ? run.closing : '(not decided yet)'}
        </pre>
      </Panel>
      {state.decided ? (
        <Facts
          values={[
            ['Rows changed', String(run.rowsChanged)],
            ['Audit row', run.audit],
            ['Writes made', String(run.rowsChanged)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.decided ? (
        <p className="rel-caption">
          {RESUME_COUNTEREXAMPLE} {RESUME_REFINEMENT}
        </p>
      ) : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ApproveAuditLab() {
  const [state, setState] = useState(initialAudit);
  const act = (action: AuditAction) => setState((current) => auditTransition(current, action));
  const summary = auditSummary(state);
  return (
    <Lab
      title="Who approved what, and did it run?"
      eyebrow="Approval · every action written down"
      className="rel-lab approve-lab"
    >
      <Predict>
        Who approved what, and did it run? Choose who asks and approves, then press Approve.
      </Predict>
      <div className="cb-controls">
        <label>
          Who asks and approves
          <ModelSelect
            aria-label="Who asks and approves"
            value={state.approver}
            onValueChange={(value) => act({ type: 'approver', value: toApprover(value) })}
            options={APPROVERS.map((approver) => ({
              value: approver,
              label: approverLabels[approver],
            }))}
          />
        </label>
        <Button size="sm" disabled={state.approved} onClick={() => act({ type: 'approve' })}>
          Approve
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The command">
        <pre aria-label="The command">{AUDIT_COMMAND}</pre>
      </Panel>
      <Table
        label="The audit row"
        columns={['Field', 'Value']}
        template="minmax(0, 0.8fr) minmax(0, 2.6fr)"
        rows={state.approved ? auditTable(state.approver) : []}
      />
      {state.approved ? (
        <Facts
          values={[
            ['Approved', 'yes'],
            ['Permitted', permitted(state.approver)],
            ['Rows changed', auditRowsChanged(state.approver)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.approved && state.approver === 'ravi' ? (
        <p className="rel-caption">{RAVI_NOTE}</p>
      ) : null}
      {state.approved ? <p className="rel-caption">{AUDIT_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
